import { h365PlatformApi } from '@/plugin/axios'

/**
 * 订阅制服务API封装
 */

/**
 * 获取订阅商品列表
 * @param {Object} params - 查询参数
 * @param {number} params.status - 商品状态筛选 (可选)
 * @returns {Promise} API响应
 * @description 返回的商品数据包含以下新增字段：
 * - firstDayRewardCoinAmount: 第一天发放金币数量 (可选, 若未设定则使用 dailyCoinAmount)
 * - isDisplay: 是否在前端显示 (API 自动过滤, 只返回 isDisplay=true 的商品)
 */
export const getSubscriptionProducts = (params = {}) => {
  return h365PlatformApi.get('/subscription/products', { params })
}

/**
 * 获取单个订阅商品详情
 * @param {string} subscriptionProductId - 订阅商品ID
 * @returns {Promise} API响应
 */
export const getSubscriptionProduct = (subscriptionProductId) => {
  return h365PlatformApi.get(`/subscription/products/${subscriptionProductId}`)
}

/**
 * 创建订阅订单 (使用新的 BillingModule API)
 * @param {Object} data - 订单数据
 * @param {string} data.subscriptionProductId - 必填：订阅商品ID
 * @param {string} data.applicationUrl - 可选：支付完成返回URL
 * @param {string} data.sdkVersion - 可选：SDK版本
 * @returns {Promise} API响应
 */
export const createSubscriptionOrder = (data) => {
  // 添加默认的 sdkVersion 如果未提供
  const requestData = {
    sdkVersion: '1.0.0',
    ...data
  }
  return h365PlatformApi.post('/billing/subscription/create-order', requestData)
}

/**
 * 创建订阅订单 (舊版API - 向後兼容)
 * @param {Object} data - 订单数据
 * @param {string} data.subscriptionProductId - 必填：订阅商品ID
 * @param {string} data.applicationUrl - 可选：支付完成返回URL
 * @param {string} data.sdkVersion - 可选：SDK版本
 * @returns {Promise} API响应
 */
export const createSubscriptionOrderLegacy = (data) => {
  return h365PlatformApi.post('/subscription/order', data)
}

/**
 * 获取用户订阅列表
 * @param {Object} data - 查询参数
 * @param {number} data.status - 可选：订阅状态筛选
 * @param {number} data.pageSize - 可选：每页数量，默认10
 * @param {number} data.currentPage - 可选：当前页码，默认1
 * @returns {Promise} API响应
 * @description 返回的订阅数据现在包含从 SubscriptionProduct JOIN 的商品信息：
 * - productName: 商品名称
 * - description: 商品描述
 * - imageUrl: 商品图片URL
 * - unitPrice: 单价
 * - currency: 币别
 * 注意：已购买的订阅不受商品 isDisplay 状态影响，即使商品已下架仍可查看
 */
export const getUserSubscriptions = (data = {}) => {
  return h365PlatformApi.post('/subscription/my-subscriptions', data)
}

/**
 * 查询订阅购买状态
 * @param {string} userSubscriptionId - 用户订阅记录ID
 * @returns {Promise} API响应
 */
export const getSubscriptionStatus = (userSubscriptionId) => {
  return h365PlatformApi.get(`/subscription/status/${userSubscriptionId}`)
}

/**
 * 手动触发金币发放 (使用新的 BillingModule API)
 * @returns {Promise} API响应
 */
export const distributeCoins = () => {
  return h365PlatformApi.post('/billing/subscription/distribute-coins')
}

/**
 * 手动触发金币发放 (舊版API - 向後兼容)
 * @returns {Promise} API响应
 */
export const distributeCoinsLegacy = () => {
  return h365PlatformApi.post('/subscription/distribute-coins')
}

/**
 * 激活订阅 (使用新的 BillingModule API)
 * @param {string} userSubscriptionId - 用户订阅记录ID
 * @returns {Promise} API响应
 */
export const activateSubscription = (userSubscriptionId) => {
  return h365PlatformApi.post('/billing/subscription/activate', {
    userSubscriptionId
  })
}

/**
 * 激活订阅 (舊版API - 向後兼容)
 * @param {string} userSubscriptionId - 用户订阅记录ID
 * @returns {Promise} API响应
 */
export const activateSubscriptionLegacy = (userSubscriptionId) => {
  return h365PlatformApi.put(`/subscription/activate/${userSubscriptionId}`)
}

// 管理员API (需要相应权限)

/**
 * 创建订阅商品 (管理员)
 * @param {Object} data - 商品数据
 * @returns {Promise} API响应
 */
export const createSubscriptionProduct = (data) => {
  return h365PlatformApi.post('/subscription/admin/products', data)
}

/**
 * 更新订阅商品 (管理员)
 * @param {string} subscriptionProductId - 订阅商品ID
 * @param {Object} data - 更新数据
 * @returns {Promise} API响应
 */
export const updateSubscriptionProduct = (subscriptionProductId, data) => {
  return h365PlatformApi.put(`/subscription/admin/products/${subscriptionProductId}`, data)
}

/**
 * 删除订阅商品 (管理员)
 * @param {string} subscriptionProductId - 订阅商品ID
 * @returns {Promise} API响应
 */
export const deleteSubscriptionProduct = (subscriptionProductId) => {
  return h365PlatformApi.delete(`/subscription/admin/products/${subscriptionProductId}`)
}